import { motion } from "framer-motion";
import { MapPin, Lock, Truck, Battery, Grid3X3, Building2, CheckCircle2, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ScrollReveal } from "@/components/animations/ScrollAnimations";

const showcaseItems = [
  {
    id: "public-detail",
    number: "01",
    title: "Public Charging",
    headline: "High-Visibility Infrastructure",
    description:
      "Deploy and operate public EV charging stations that attract customers, generate revenue, and support the growing EV ecosystem.",
    features: [
      "Strategic placement at retail, highways, urban hubs",
      "Multiple payment options including roaming networks",
      "DC fast charging up to 1 Megawatt",
      "Real-time availability and reservation system",
      "24/7 driver support and maintenance",
    ],
    stats: [
      { value: "350,000+", label: "Drivers Reached" },
      { value: "99.5%", label: "Uptime SLA" },
    ],
    icon: MapPin,
    isPrimary: true,
  },
  {
    id: "private-detail",
    number: "02",
    title: "Private Charging",
    headline: "Controlled Access Solutions",
    description:
      "Workplace and residential charging with complete access control, usage tracking, and flexible billing options.",
    features: [
      "RFID and app-based access control",
      "Employee and tenant billing management",
      "Reservation and scheduling system",
      "Usage analytics and reporting",
      "Integration with building management",
    ],
    stats: [
      { value: "100%", label: "Access Control" },
      { value: "Flexible", label: "Billing" },
    ],
    icon: Lock,
    isPrimary: false,
  },
  {
    id: "fleet-detail",
    number: "03",
    title: "Fleet Management",
    headline: "Depot Optimization & TCO",
    description:
      "Smart charging solutions for commercial fleets with route optimization, cost management, and operational analytics.",
    features: [
      "Intelligent depot charging optimization",
      "Route planning with charging stops",
      "Total Cost of Ownership analytics",
      "Driver assignment and scheduling",
      "Integration with fleet management systems",
    ],
    stats: [
      { value: "40%", label: "Cost Reduction" },
      { value: "ROI", label: "12-18 Months" },
    ],
    icon: Truck,
    isPrimary: true,
    video: "/videos/fleet-charging.mp4",
  },
  {
    id: "v2g-detail",
    number: "04",
    title: "V2G Technology",
    headline: "Vehicles as Grid Assets",
    description:
      "Transform your EV fleet into revenue-generating grid resources through bidirectional charging and energy services.",
    features: [
      "Bidirectional charging infrastructure",
      "Energy arbitrage optimization",
      "Demand response participation",
      "Vehicle-to-Home (V2H) capability",
      "Grid services revenue sharing",
    ],
    stats: [
      { value: "$5K+", label: "Annual Revenue/Vehicle" },
      { value: "50+", label: "Compatible Models" },
    ],
    icon: Battery,
    isPrimary: false,
    video: "/videos/v2g-technology.mp4",
  },
  {
    id: "grid-detail",
    number: "05",
    title: "Grid Integration",
    headline: "Smart Load Management",
    description: "Certified grid integration solutions for demand response, load management, and utility partnerships.",
    features: [
      "OpenADR 2.0 certified",
      "OCPP 2.0.1 compliant",
      "Demand response automation",
      "Dynamic load balancing",
      "Utility program integration",
    ],
    stats: [
      { value: "OpenADR", label: "Certified" },
      { value: "Real-time", label: "Grid Response" },
    ],
    icon: Grid3X3,
    isPrimary: true,
    image: "/images/grid-integration.gif",
  },
  {
    id: "cpo-detail",
    number: "06",
    title: "CPO Services",
    headline: "End-to-End Turnkey",
    description:
      "Complete Charge Point Operator services from site selection to ongoing maintenance, branded with your identity.",
    features: [
      "Site selection and design",
      "Equipment procurement and installation",
      "Network operations and monitoring",
      "Customer support and maintenance",
      "Revenue management and reporting",
    ],
    stats: [
      { value: "Full", label: "Turnkey Solution" },
      { value: "Your Brand", label: "Our Expertise" },
    ],
    icon: Building2,
    isPrimary: false,
  },
];

export function SolutionsShowcase() {
  return (
    <section className="relative">
      {/* Section Divider */}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-border/50 to-transparent" />
      
      {/* Section Intro */}
      <div className="container mx-auto px-4 py-32">
        <ScrollReveal>
          <div className="max-w-4xl">
            <span className="inline-flex items-center gap-2 glass-card px-5 py-2.5 rounded-full text-sm font-medium text-primary mb-8">
              Detailed Solutions
            </span>
            <h2 className="text-4xl md:text-6xl font-bold mb-6 tracking-tight">
              Deep Dive into <span className="text-gradient">Each Solution</span>
            </h2>
            <p className="text-muted-foreground max-w-2xl text-lg md:text-xl leading-relaxed">
              Explore the details of each solution and discover how they can transform your charging infrastructure.
            </p>
          </div>
        </ScrollReveal>
      </div>

      {/* Showcase Items */}
      {showcaseItems.map((item, idx) => (
        <div 
          key={item.id} 
          id={item.id} 
          className={`py-24 relative ${idx % 2 === 0 ? "bg-card/20" : "bg-background"}`}
        >
          {/* Background accent */}
          <div className="absolute inset-0 overflow-hidden">
            <motion.div 
              className={`absolute ${idx % 2 === 0 ? 'right-0' : 'left-0'} top-1/2 -translate-y-1/2 w-[500px] h-[500px] ${item.isPrimary ? 'bg-primary/5' : 'bg-accent/5'} rounded-full blur-[150px]`}
              animate={{ 
                opacity: [0.3, 0.5, 0.3]
              }}
              transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
            />
          </div>

          <div className="container mx-auto px-4 relative z-10">
            <div
              className={`grid lg:grid-cols-2 gap-16 lg:gap-24 items-center ${idx % 2 === 1 ? "lg:grid-flow-col-dense" : ""}`}
            >
              {/* Content Side */}
              <ScrollReveal className={idx % 2 === 1 ? "lg:col-start-2" : ""}>
                <div className={`flex items-center gap-5 mb-8 ${idx % 2 === 1 ? "lg:justify-end lg:flex-row-reverse" : ""}`}>
                  <span className="text-7xl font-bold text-primary/10">{item.number}</span>
                  <div className={`w-14 h-14 rounded-2xl ${item.isPrimary ? 'bg-primary/10' : 'bg-accent/10'} flex items-center justify-center`}>
                    <item.icon className={`h-7 w-7 ${item.isPrimary ? 'text-primary' : 'text-accent'}`} />
                  </div>
                </div>

                <div className={idx % 2 === 1 ? "lg:text-right" : ""}>
                  <span className={`${item.isPrimary ? 'text-primary' : 'text-accent'} font-semibold uppercase tracking-widest text-sm`}>{item.title}</span>
                  <h3 className="text-3xl md:text-5xl font-bold mt-3 mb-5">{item.headline}</h3>
                  <p className="text-muted-foreground text-lg mb-10 leading-relaxed">{item.description}</p>
                </div>

                {/* Features List */}
                <div className={`space-y-4 mb-10 ${idx % 2 === 1 ? "lg:flex lg:flex-col lg:items-end" : ""}`}>
                  {item.features.map((feature, fIdx) => (
                    <motion.div 
                      key={fIdx} 
                      className={`flex items-start gap-4 group ${idx % 2 === 1 ? "lg:flex-row-reverse lg:text-right" : ""}`}
                      initial={{ opacity: 0, x: idx % 2 === 1 ? 20 : -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ delay: fIdx * 0.08 }}
                      viewport={{ once: true }}
                    >
                      <div className={`w-8 h-8 rounded-xl ${item.isPrimary ? 'bg-primary/10 group-hover:bg-primary/20' : 'bg-accent/10 group-hover:bg-accent/20'} flex items-center justify-center flex-shrink-0 transition-colors`}>
                        <CheckCircle2 className={`h-4 w-4 ${item.isPrimary ? 'text-primary' : 'text-accent'}`} />
                      </div>
                      <span className="text-foreground/90 font-medium">{feature}</span>
                    </motion.div>
                  ))}
                </div>

                <div className={idx % 2 === 1 ? "lg:text-right" : ""}>
                  <Button variant="hero" className="group">
                    Learn More
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </Button>
                </div>
              </ScrollReveal>

              {/* Media Side */}
              <ScrollReveal delay={0.2} className={`${idx % 2 === 1 ? "lg:col-start-1 lg:row-start-1" : ""}`}>
                <div className="relative">
                  {/* Large Background Number */}
                  <div className="absolute -top-10 -left-10 text-[12rem] md:text-[16rem] font-bold text-primary/5 leading-none select-none pointer-events-none">
                    {item.number}
                  </div>

                  {/* Video if available */}
                  {item.video ? (
                    <motion.div 
                      className="relative z-10 rounded-3xl overflow-hidden border border-border/30 shadow-2xl glass-card p-2"
                      whileHover={{ scale: 1.02 }}
                      transition={{ duration: 0.3 }}
                    >
                      <video autoPlay loop muted playsInline className="w-full h-auto rounded-2xl">
                        <source src={item.video} type="video/mp4" />
                      </video>
                      {/* Stats overlay on video */}
                      <div className="absolute bottom-2 left-2 right-2 p-5 bg-gradient-to-t from-background/95 via-background/80 to-transparent rounded-b-2xl">
                        <div className="flex gap-6 justify-center">
                          {item.stats.map((stat, sIdx) => (
                            <div key={sIdx} className="text-center">
                              <div className={`text-2xl md:text-3xl font-bold ${item.isPrimary ? 'text-primary' : 'text-accent'}`}>{stat.value}</div>
                              <div className="text-xs text-muted-foreground uppercase tracking-wider font-medium">{stat.label}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </motion.div>
                  ) : item.image ? (
                    /* Image/GIF for items with image */
                    <motion.div 
                      className="relative z-10 rounded-3xl overflow-hidden border border-border/30 shadow-2xl glass-card p-2"
                      whileHover={{ scale: 1.02 }}
                      transition={{ duration: 0.3 }}
                    >
                      <img src={item.image} alt={item.title} className="w-full h-auto rounded-2xl" />
                      {/* Stats overlay on image */}
                      <div className="absolute bottom-2 left-2 right-2 p-5 bg-gradient-to-t from-background/95 via-background/80 to-transparent rounded-b-2xl">
                        <div className="flex gap-6 justify-center">
                          {item.stats.map((stat, sIdx) => (
                            <div key={sIdx} className="text-center">
                              <div className={`text-2xl md:text-3xl font-bold ${item.isPrimary ? 'text-primary' : 'text-accent'}`}>{stat.value}</div>
                              <div className="text-xs text-muted-foreground uppercase tracking-wider font-medium">{stat.label}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </motion.div>
                  ) : (
                    /* Stats Cards for items without video or image */
                    <div className="grid grid-cols-2 gap-6 relative z-10">
                      {item.stats.map((stat, sIdx) => (
                        <motion.div
                          key={sIdx}
                          className="glass-card rounded-3xl p-8 md:p-10 text-center group cursor-default"
                          whileHover={{ scale: 1.03, y: -6 }}
                        >
                          <div className={`text-4xl md:text-5xl font-bold ${item.isPrimary ? 'text-primary' : 'text-accent'} mb-3`}>{stat.value}</div>
                          <div className="text-sm text-muted-foreground uppercase tracking-wider font-medium">{stat.label}</div>
                        </motion.div>
                      ))}
                    </div>
                  )}

                  {/* Decorative Elements */}
                  <div className={`absolute -bottom-6 -right-6 w-32 h-32 ${item.isPrimary ? 'bg-primary/10' : 'bg-accent/10'} rounded-full blur-3xl pointer-events-none`} />
                </div>
              </ScrollReveal>
            </div>
          </div>
        </div>
      ))}
    </section>
  );
}
